import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Chip, CircularProgress, TextField,
  Card, CardContent, CardHeader, IconButton, Divider, Alert, LinearProgress,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import TimerIcon from '@mui/icons-material/Timer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';

const PlanningView = ({ project, onRefresh, onBack }) => {
  const [shootDays, setShootDays] = useState(project.shoot_days || []);
  const [planning, setPlanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const autoPlanned = useRef(false);

  // Sync shoot days when project data refreshes
  useEffect(() => {
    setShootDays(project.shoot_days || []);
  }, [project]);

  // Auto-plan when arriving with no shoot days
  useEffect(() => {
    if (shootDays.length === 0 && !autoPlanned.current) {
      autoPlanned.current = true;
      doAutoPlan();
    }
    // eslint-disable-next-line
  }, []);

  const doAutoPlan = async () => {
    setPlanning(true);
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}/auto-plan`, {
        method: 'POST',
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.detail || 'Planning mislukt');
      }
      const data = await resp.json();
      toast.success(`${data.shoot_days_created} draaidagen aangemaakt`);
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPlanning(false);
    }
  };

  const handleAutoPlan = async () => {
    if (shootDays.length > 0) {
      if (!(await bevestig({ titel: 'Planning overschrijven?', tekst: 'De bestaande planning wordt vervangen door een nieuwe automatische planning.', knop: 'Overschrijven' }))) return;
    }
    await doAutoPlan();
  };

  const updateDay = (idx, field, value) => {
    const updated = [...shootDays];
    updated[idx] = { ...updated[idx], [field]: value };
    setShootDays(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}/shoot-days`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(shootDays),
      });
      if (!resp.ok) throw new Error('Opslaan mislukt');
      toast.success('Planning opgeslagen');
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (dayId) => {
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/script-breakdown/projects/${project.id}/export/${dayId}`);
      if (!resp.ok) throw new Error('Export mislukt');
      const data = await resp.json();

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `draaidag-${data.day_number}-${data.location || 'export'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export gedownload');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const hasDays = shootDays.length > 0;
  const totalShots = shootDays.reduce((sum, d) => sum + (d.shots?.length || 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Draaidagen Planning
          </Typography>
          {hasDays && (
            <Typography variant="body2" color="text.secondary">
              {shootDays.length} draaidagen | {totalShots} shots
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small"
            startIcon={planning ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            onClick={handleAutoPlan} disabled={planning}
            variant={hasDays ? 'text' : 'contained'}
            sx={!hasDays ? { bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } } : {}}>
            {hasDays ? 'Opnieuw plannen' : 'Auto-plan'}
          </Button>
          {hasDays && (
            <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave} disabled={saving}>
              Opslaan
            </Button>
          )}
        </Box>
      </Box>

      {planning && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <LinearProgress sx={{ mb: 1, '& .MuiLinearProgress-bar': { bgcolor: '#7c3aed' } }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Planning wordt berekend...
          </Typography>
        </Paper>
      )}

      {!hasDays && !planning && (
        <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <Typography color="text.secondary" gutterBottom>
            Nog geen draaidagen. De planning wordt automatisch berekend.
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {shootDays.map((day, idx) => {
          const cs = day.callsheet_data || {};
          const warnings = cs.warnings || [];
          const totalMin = cs.total_minutes || 0;
          const dayShots = day.shots || [];

          return (
            <Card key={day.id || idx} variant="outlined" sx={{ borderColor: 'rgba(124,58,237,0.2)' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarTodayIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
                    <Typography fontWeight={700}>Dag {day.day_number}</Typography>
                    {totalMin > 0 && (
                      <Chip icon={<TimerIcon sx={{ fontSize: 12 }} />} size="small"
                        label={cs.total_formatted || `${Math.floor(totalMin / 60)}u${(totalMin % 60).toString().padStart(2, '0')}`}
                        sx={{ fontSize: 11, ml: 'auto' }}
                        color={totalMin > 600 ? 'error' : 'default'} />
                    )}
                  </Box>
                }
                subheader={
                  <Box sx={{ mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">{day.location || 'Geen locatie'}</Typography>
                    </Box>
                    {/* Callsheet timeline */}
                    {cs.crew_call && (
                      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                        <Chip icon={<AccessTimeIcon sx={{ fontSize: 12 }} />} label={`Call: ${cs.crew_call}`}
                          size="small" variant="outlined" sx={{ fontSize: 10, height: 22 }} />
                        <Chip icon={<VideocamIcon sx={{ fontSize: 12 }} />} label={`1e shot: ${cs.first_shot}`}
                          size="small" variant="outlined" sx={{ fontSize: 10, height: 22 }} />
                        {cs.lunch && (
                          <Chip icon={<RestaurantIcon sx={{ fontSize: 12 }} />} label={`Lunch: ${cs.lunch}`}
                            size="small" variant="outlined" sx={{ fontSize: 10, height: 22 }} />
                        )}
                        <Chip icon={<AccessTimeIcon sx={{ fontSize: 12 }} />} label={`Wrap: ${cs.wrap}`}
                          size="small" variant="outlined" sx={{ fontSize: 10, height: 22 }} />
                      </Box>
                    )}
                  </Box>
                }
                action={
                  <IconButton size="small" onClick={() => handleExport(day.id)} title="Export callsheet">
                    <DownloadIcon />
                  </IconButton>
                }
                sx={{ pb: 1 }}
              />

              {/* Warnings */}
              {warnings.length > 0 && (
                <Box sx={{ px: 2, pb: 1 }}>
                  {warnings.map((w, wi) => (
                    <Alert key={wi} severity="warning" icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                      sx={{ py: 0, px: 1, mb: 0.5, fontSize: 12, '& .MuiAlert-message': { py: 0.3 } }}>
                      {w}
                    </Alert>
                  ))}
                </Box>
              )}

              <Divider />
              <CardContent sx={{ pt: 1 }}>
                <TextField
                  size="small" label="Datum" type="date" fullWidth
                  value={day.shoot_date || ''}
                  onChange={(e) => updateDay(idx, 'shoot_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  size="small" label="Notities" fullWidth multiline maxRows={2}
                  value={day.notes || ''}
                  onChange={(e) => updateDay(idx, 'notes', e.target.value)}
                  sx={{ mb: 1.5 }}
                />

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Shots ({dayShots.length})
                </Typography>

                {dayShots.map((shot, si) => (
                  <Paper key={shot.id || si} variant="outlined"
                    sx={{ p: 1, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(0,0,0,0.01)' }}>
                    <VideocamIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
                    <Chip label={shot.shot_number || `#${si + 1}`} size="small" sx={{ fontWeight: 600 }} />
                    <Typography variant="body2" sx={{ flexGrow: 1, fontSize: 13 }} noWrap>
                      {shot.shot_description || shot.description || ''}
                    </Typography>
                    {shot.setup_time_minutes > 0 && (
                      <Chip icon={<TimerIcon sx={{ fontSize: 11 }} />}
                        label={`${shot.setup_time_minutes}m`}
                        size="small" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    )}
                    <Chip label={`Sc ${shot.scene_number}`} size="small" variant="outlined"
                      sx={{ fontSize: 11 }} />
                  </Paper>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button startIcon={<NavigateBeforeIcon />} onClick={onBack}>Terug</Button>
      </Box>
    </Box>
  );
};

export default PlanningView;
